import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { buildAndDownloadApkg } from '../../utils/anki';

export default function DeckDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { decks, savedWords, removeWord, deleteDeck } = useAppContext();
  const deck = decks.find(d => d.id === id);
  const words = savedWords.filter(w => w.deckId === id);
  const [ankiDownloading, setAnkiDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!deck) {
    return (
      <div className="p-4 pt-14 md:p-8 text-center">
        <p className="text-slate-500 dark:text-slate-400">Deck not found.</p>
        <button onClick={() => navigate('/app/decks')} className="mt-4 text-primary-600 hover:text-primary-700 text-sm">
          Back to Decks
        </button>
      </div>
    );
  }

  const handleExportAnki = async () => {
    if (words.length === 0) return;
    setAnkiDownloading(true);
    try {
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const exportWords = words.map(w => ({ surface: w.text, reading: w.reading, translation: w.translation, exampleSentence: w.exampleSentence }));
      await buildAndDownloadApkg(exportWords, deck.name, `${deck.name.replace(/\s+/g, '_')}_${stamp}.apkg`);
    } finally {
      setAnkiDownloading(false);
    }
  };

  const handleDelete = () => {
    deleteDeck(deck.id);
    navigate('/app/decks');
  };

  return (
    <div className="p-4 pt-14 md:p-8 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/app/decks')}
        className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors mb-6"
      >
        ← Back to Decks
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{deck.name}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{words.length} word{words.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {words.length > 0 && (
            <>
              <button
                onClick={() => navigate(`/app/decks/${deck.id}/review`)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors font-medium"
              >
                Review
              </button>
              <button
                onClick={handleExportAnki}
                disabled={ankiDownloading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors font-medium disabled:bg-slate-300 dark:disabled:bg-slate-600"
              >
                {ankiDownloading ? 'Preparing...' : 'Export Anki'}
              </button>
            </>
          )}
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm rounded-lg transition-colors border border-red-200 dark:border-red-700"
            >
              Delete
            </button>
          ) : (
            <div className="flex gap-1">
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {words.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="text-3xl mb-3">📝</div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">No words in this deck yet.</p>
          <p className="text-slate-400 text-xs mt-1">Save words from articles to add them here.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {words.map(word => (
              <li key={word.id} className="px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                <div className="flex items-baseline gap-4">
                  <span className="font-semibold text-slate-800 dark:text-slate-100 min-w-[3em]">{word.text}</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400 min-w-[4em]">{word.reading}</span>
                  <span className="text-sm text-slate-400 flex-1">{word.translation}</span>
                  <button
                    onClick={() => removeWord(word.id)}
                    className="text-xs text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors px-2 py-1"
                  >
                    Remove
                  </button>
                </div>
                {word.exampleSentence && (
                  <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 italic pl-[3em]">
                    {word.exampleSentence}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
